'use client';
import {type SightCategory} from '#/cms/schemas/sight-category.schema';
import {type Sight} from '#/cms/schemas/sight.schema';
import {Marker} from '@react-google-maps/api';
import {useMemo, useState} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import {MapMarkerSvg} from '#/features/components/MapMarkerSvg';
import {useCurrentThemeColors} from '#/features/store/themeColorsStore';
import {MapInfoWindow} from '#/features/components/MapInfoWindow';
import SightResult from '#/features/components/SightResult';
import {latLngToGoogleLatLngLiteral} from '#/utils/geo';

const markerSize = 32;

type Props = {
  sight: Sight;
  sightCategories: SightCategory[];
};

/**
 * Map marker component for a sight.
 * Uses the sight's primary category to determine the icon to use.
 */
export const SightMapMarker = (props: Props) => {
  const {sight, sightCategories} = props;
  const themeColors = useCurrentThemeColors();
  const [infoWindowVisible, setInfoWindowVisible] = useState(false);

  const latLng = useMemo(
    () => (sight.coordinate ? latLngToGoogleLatLngLiteral(sight.coordinate) : undefined),
    [sight.coordinate]
  );

  const iconName = useMemo(() => {
    const primarySightCategoryId = sight.sightCategories?.[0]?.id;
    const primarySightCategory = sightCategories?.find(category => category.id === primarySightCategoryId);
    const iconName = primarySightCategory?.pepIcon ?? 'LocationPin';
    return iconName;
  }, [sight.sightCategories, sightCategories]);

  const icon = useMemo(() => {
    const uriBase = 'data:image/svg+xml,';
    const staticMarkup = renderToStaticMarkup(
      <MapMarkerSvg
        iconName={iconName}
        backgroundColor={themeColors.primary}
        foregroundColor={themeColors.light}
        fringeColor={themeColors.modalBackdrop}
        size={markerSize}
      />
    );
    const encodedMarkup = encodeURIComponent(staticMarkup);
    const icon = `${uriBase}${encodedMarkup}`;
    return icon;
  }, [iconName, themeColors]);

  const handleMarkerClick = () => {
    setInfoWindowVisible(!infoWindowVisible);
  };

  if (!latLng) return null;

  return (
    <>
      <Marker position={latLng} icon={icon} onClick={handleMarkerClick} />
      <MapInfoWindow
        isVisible={infoWindowVisible}
        position={latLng}
        markerSize={markerSize}
        onCloseClick={() => setInfoWindowVisible(false)}
        Content={<SightResult sight={sight} />}
      />
    </>
  );
};
